/** tm/components/DueDatePicker.tsx — 기한 선택 드롭다운 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { C, T, R } from './ui';

interface Props {
  value: string;
  onChange: (date: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
}

const QUICK = [7, 14, 21, 28, 35, 42, 49, 56, 63, 70];

function addDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function diffDays(dateStr: string): number | null {
  if (!dateStr || dateStr.length < 10) return null;
  const d = new Date(dateStr + 'T00:00:00');
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - now.getTime()) / 86400000);
}

export default function DueDatePicker({ value, onChange, placeholder, style }: Props) {
  const [open, setOpen] = useState(false);
  const [customDays, setCustomDays] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [popRight, setPopRight] = useState(false);
  const [popUp, setPopUp] = useState(false);

  const [popPos, setPopPos] = useState({ top: 0, left: 0 });
  const checkBounds = useCallback(() => {
    if (!rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const POP_W = 280, POP_H = 280, MARGIN = 8;
    const goUp = rect.bottom + POP_H + MARGIN > window.innerHeight;
    // 오른쪽으로 열면 잘리는 경우 → rect.right 정렬, 그래도 잘리면 우측 끝에 붙이기
    let left = rect.left;
    if (left + POP_W > window.innerWidth - MARGIN) {
      left = Math.max(MARGIN, rect.right - POP_W);
    }
    if (left < MARGIN) left = MARGIN;
    const goRight = left !== rect.left;
    setPopRight(goRight);
    setPopUp(goUp);
    setPopPos({
      top: goUp ? rect.top - MARGIN : rect.bottom + MARGIN,
      left,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    checkBounds();
    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (rootRef.current && rootRef.current.contains(target)) return;
      if (popRef.current && popRef.current.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [open, checkBounds]);

  function pick(days: number) { onChange(addDays(days)); setOpen(false); }
  function applyCustom() {
    const n = parseInt(customDays, 10);
    if (n > 0) { onChange(addDays(n)); setOpen(false); setCustomDays(''); }
  }

  const diff = diffDays(value);
  const badge = diff !== null && !isNaN(diff) ? (diff === 0 ? 'D-Day' : diff > 0 ? `+${diff}d` : `${diff}d`) : null;
  const badgeColor = diff !== null && diff < 0 ? C.red : diff !== null && diff <= 3 ? C.orange : C.accent;
  const badgeBg = diff !== null && diff < 0 ? C.redLt : diff !== null && diff <= 3 ? C.orangeLt : C.accentLt;

  return (
    <div ref={rootRef} style={{ position: 'relative', ...style }}>
      {/* ── 트리거 ── */}
      <div
        onClick={() => setOpen(!open)}
        style={{
          padding: '6px 10px', borderRadius: R.sm,
          border: `1px solid ${open ? C.accent : C.border}`,
          background: C.white, cursor: 'pointer', fontSize: T.xs,
          minWidth: 110, textAlign: 'left', boxSizing: 'border-box' as const,
          transition: 'border-color 0.15s', width: '100%',
        }}
      >
        {value && badge ? (
          <span style={{
            display: 'inline-block', fontSize: T.xs, fontWeight: 700,
            color: badgeColor, background: badgeBg,
            padding: '2px 6px', borderRadius: 10, lineHeight: '14px',
          }}>{badge}</span>
        ) : (
          <span style={{ color: C.muted }}>{placeholder || '기한'}</span>
        )}
      </div>

      {/* ── 드롭다운 팝업 ── */}
      {open && (
        <div ref={popRef} style={{
          position: 'fixed',
          top: popUp ? undefined : popPos.top,
          bottom: popUp ? (window.innerHeight - popPos.top) : undefined,
          left: popPos.left,
          zIndex: 9999,
          width: 280, background: C.white, borderRadius: R.md,
          border: `1px solid ${C.border}`, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          padding: 12,
        }}>
          {/* 퀵선택 */}
          <div style={{ fontSize: T.xs, fontWeight: 600, color: C.muted, marginBottom: 6 }}>퀵 선택</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
            {QUICK.map(d => (
              <button key={d} onClick={() => pick(d)}
                style={{
                  padding: '5px 10px', borderRadius: R.sm, fontSize: T.xs, fontWeight: 600,
                  cursor: 'pointer', border: `1px solid ${C.border}`, background: C.white,
                  color: C.sub, transition: 'all 0.1s',
                }}
                onMouseEnter={e => { const t = e.currentTarget; t.style.background = C.accentLt; t.style.borderColor = C.accent; t.style.color = C.accent; }}
                onMouseLeave={e => { const t = e.currentTarget; t.style.background = C.white; t.style.borderColor = C.border; t.style.color = C.sub; }}>
                +{d}일
              </button>
            ))}
          </div>

          <div style={{ height: 1, background: C.border, margin: '2px 0 10px' }} />

          {/* 날짜 선택 */}
          <div style={{ fontSize: T.xs, fontWeight: 600, color: C.muted, marginBottom: 6 }}>날짜 선택</div>
          <style dangerouslySetInnerHTML={{ __html: '.dtp-date::-webkit-inner-spin-button,.dtp-date::-webkit-calendar-picker-indicator~::-webkit-inner-spin-button{display:none}.dtp-date::-webkit-clear-button{display:none}' }} />
          <input type="date" className="dtp-date" value={value} max="9999-12-31"
            onChange={e => {
              const v = e.target.value;
              // YYYY-MM-DD (4자리 연도) 만 반영
              if (/^\d{4}-\d{2}-\d{2}$/.test(v) && Number(v.slice(0,4)) <= 9999) {
                onChange(v);
              } else if (v === '') {
                onChange('');
              }
            }}
            onBlur={e => {
              // 포커스가 빠질 때 값이 완성되어 있으면 팝업 닫기
              if (/^\d{4}-\d{2}-\d{2}$/.test(e.target.value)) {
                setTimeout(() => setOpen(false), 100);
              }
            }}
            style={{ width: '100%', padding: '6px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none', boxSizing: 'border-box' as const }} />

          {value && (
            <button onClick={() => { onChange(''); setOpen(false); }}
              style={{ width: '100%', marginTop: 8, padding: '5px 0', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.muted, fontSize: T.xs, cursor: 'pointer' }}>
              초기화
            </button>
          )}
        </div>
      )}
    </div>
  );
}
